import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { getSessionLandingPath, SESSION_MASTER_TOKEN_URL } from '../config/sessionAppData';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import {
  readFranchiseFlowAuth,
  hasFranchiseFlowAuth,
  franchiseAuthHeaders,
  getKioskBootstrapToken,
} from '../../shared/franchiseFlowAuth';
import {
  getSalonAppSessionToken,
  setSalonAppSessionToken,
} from '../utils/salonAppSessionToken';

const SkinSessionContext = createContext(null);

function readTokenFromLocationSearch() {
  try {
    const params = new URLSearchParams(window.location.search || '');
    return (
      params.get('token') ||
      params.get('app_session_token') ||
      params.get('public_token') ||
      ''
    ).trim();
  } catch {
    return '';
  }
}

export function SkinSessionProvider({ children }) {
  const [token, setTokenState] = useState(() => {
    const fromUrl = readTokenFromLocationSearch();
    if (fromUrl) return fromUrl;
    return getSalonAppSessionToken();
  });
  const [tokenReady, setTokenReady] = useState(() =>
    Boolean(readTokenFromLocationSearch() || getSalonAppSessionToken()),
  );

  const setToken = useCallback((value) => {
    const next = typeof value === 'string' ? value.trim() : '';
    setTokenState(next);
    setSalonAppSessionToken(next);
  }, []);

  // Priority: URL ?token= → in-memory → franchise bootstrap → salon-admin /session/master-token
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const fromUrl = readTokenFromLocationSearch();
      if (fromUrl) {
        if (!cancelled) {
          setToken(fromUrl);
          setTokenReady(true);
        }
        return;
      }

      const stored = getSalonAppSessionToken();
      if (stored) {
        if (!cancelled) {
          setToken(stored);
          setTokenReady(true);
        }
        return;
      }

      const franchiseBootstrap =
        String(getKioskBootstrapToken() || readFranchiseFlowAuth()?.kiosk_bootstrap_token || '').trim();
      if (franchiseBootstrap) {
        if (!cancelled) {
          setToken(franchiseBootstrap);
          setTokenReady(true);
        }
        return;
      }

      try {
        const res = await fetchWithRetry(SESSION_MASTER_TOKEN_URL, {
          credentials: hasFranchiseFlowAuth() ? 'include' : 'same-origin',
          headers: hasFranchiseFlowAuth() ? franchiseAuthHeaders() : {},
          timeoutMs: 12000,
        }, { label: 'session-master-token' });
        const data = await res.json().catch(() => ({}));
        const master = String(data?.public_token || '').trim();
        if (!cancelled && res.ok && master) {
          setToken(master);
        }
      } catch (_) {
        // Landing/hub can still render; pages will retry via tokenReady=false consumers if needed.
      } finally {
        if (!cancelled) setTokenReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [setToken]);

  const sessionLandingPath = useMemo(() => getSessionLandingPath(token), [token]);

  return (
    <SkinSessionContext.Provider value={{ token, setToken, sessionLandingPath, tokenReady }}>
      {children}
    </SkinSessionContext.Provider>
  );
}

export function useSkinSession() {
  const ctx = useContext(SkinSessionContext);
  if (!ctx) {
    throw new Error('useSkinSession must be used within SkinSessionProvider');
  }
  return ctx;
}

/** Returns null when rendered outside SkinSessionProvider (e.g. franchise login). */
export function useOptionalSkinSession() {
  return useContext(SkinSessionContext);
}
